<template>
    <div class="container">
        <SiteHeader></SiteHeader>

        <div class="handoff-container">
            <!-- Loading State -->
            <div v-if="loadingState" class="loading-section">
                <div class="loading-spinner"></div>
                <h3>Authenticating...</h3>
                <p>Please wait while we verify your authentication token.</p>
            </div>

            <!-- Error State -->
            <div v-else-if="errorState" class="error-section">
                <div class="error-icon">⚠️</div>
                <h3>Authentication Failed</h3>
                <div class="error-message">
                    <p>{{ errorMessage }}</p>
                    <div v-if="errorDetails" class="error-details">
                        <small>{{ errorDetails }}</small>
                    </div>
                </div>
                
                <!-- Error-specific actions -->
                <div class="error-actions">
                    <button 
                        v-if="showRetryButton" 
                        @click="retryHandoff" 
                        class="btn btn-primary"
                        :disabled="retryLoading"
                    >
                        {{ retryLoading ? 'Retrying...' : 'Retry Authentication' }}
                    </button>
                    
                    <button @click="redirectToHome" class="btn btn-secondary">
                        Return to Home
                    </button>
                </div>
            </div>

            <!-- Success State (brief display before redirect) -->
            <div v-else-if="successState" class="success-section">
                <div class="success-icon">✅</div>
                <h3>Authentication Successful</h3>
                <p>Welcome! Redirecting you to the trim editor...</p>
                <div class="loading-spinner small"></div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import SiteHeader from "../components/SiteHeader.vue";
import API from "../../API";

export default {
    name: "HandoffView",

    components: {
        SiteHeader,
    },

    data() {
        return {
            loadingState: true,
            errorState: false,
            successState: false,
            errorMessage: '',
            errorDetails: '',
            showRetryButton: false,
            retryLoading: false,
            token: null,
            redirectUrl: null,
        }
    },

    computed: {
        // Analytics data for tracking handoff events
        analyticsData() {
            return {
                token: this.token ? this.token.substring(0, 4) + '...' : null,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString(),
                referrer: document.referrer,
            };
        }
    },

    methods: {
        ...mapActions('authentication', [
            'establishSupabaseSession',
        ]),

        async processHandoffToken() {
            try {
                this.loadingState = true;
                this.errorState = false;
                this.successState = false;

                // Get token from URL query parameter
                const token = this.$route.query.token;
                
                if (!token) {
                    this.handleError('Missing authentication token', 'No token parameter found in the URL.', false);
                    this.trackAnalytics('handoff_error', { error: 'missing_token' });
                    return;
                }

                this.token = token;

                // Validate token format (should be 12 characters)
                if (typeof token !== 'string' || token.length !== 12) {
                    this.handleError('Invalid token format', 'The authentication token appears to be malformed.', false);
                    this.trackAnalytics('handoff_error', { error: 'invalid_token_format' });
                    return;
                }

                console.log('Processing handoff token:', token.substring(0, 4) + '...');
                this.trackAnalytics('handoff_started');

                // Call backend validation endpoint
                const response = await API.authentication.validateHandoff(token);

                if (response.success) {
                    this.successState = true;
                    this.loadingState = false;
                    
                    // Extract session data including customer context
                    const { user, session, customer_context, redirectUrl } = response;
                    this.redirectUrl = redirectUrl;

                    const customerId = customer_context?.customer_id;
                    console.log(`Handoff validation successful for user: ${user.email}${customerId ? ` with customer context: ${customerId}` : ''}`);
                    this.trackAnalytics('handoff_success', { 
                        userId: user.id, 
                        hasCustomerContext: !!customerId,
                        customerId: customerId || null
                    });

                    // Establish Supabase session with customer context
                    await this.establishSupabaseSession({
                        user,
                        session,
                        customer_context, // Pass customer context to store
                        fromHandoff: true
                    });

                    // Redirect after brief success display
                    setTimeout(() => {
                        this.performRedirect();
                    }, 1500);

                } else {
                    this.handleError('Authentication validation failed', response.message || 'Unknown error occurred', true);
                    this.trackAnalytics('handoff_error', { error: 'validation_failed' });
                }

            } catch (error) {
                console.error('Handoff error:', error);
                
                // Handle specific error types
                let message = 'Unable to authenticate at this time.';
                let details = '';
                let allowRetry = true;

                if (error.response) {
                    const status = error.response.status;
                    const data = error.response.data;

                    switch (status) {
                        case 401:
                            if (data.error === 'Token expired') {
                                message = 'Your authentication link has expired.';
                                details = 'Please request a new authentication link from your FlutterFlow app.';
                                allowRetry = false;
                            } else if (data.error === 'Token already used') {
                                message = 'This authentication link has already been used.';
                                details = 'Each authentication link can only be used once. Please request a new one.';
                                allowRetry = false;
                            } else {
                                message = 'Invalid authentication token.';
                                details = 'The token may be incorrect or expired.';
                                allowRetry = true;
                            }
                            break;
                        case 429:
                            message = 'Too many authentication attempts.';
                            details = 'Please wait a moment before trying again.';
                            allowRetry = true;
                            break;
                        case 500:
                            message = 'Authentication service is temporarily unavailable.';
                            details = 'Please try again in a few moments.';
                            allowRetry = true;
                            break;
                        default:
                            message = data.message || message;
                            details = data.error || 'Network error occurred.';
                    }
                } else if (error.request) {
                    message = 'Unable to connect to authentication service.';
                    details = 'Please check your internet connection and try again.';
                    allowRetry = true;
                } else {
                    message = 'An unexpected error occurred.';
                    details = error.message;
                    allowRetry = true;
                }

                this.handleError(message, details, allowRetry);
                this.trackAnalytics('handoff_error', { 
                    error: 'exception', 
                    status: error.response?.status,
                    message: error.message 
                });
            }
        },

        handleError(message, details, allowRetry) {
            this.loadingState = false;
            this.errorState = true;
            this.successState = false;
            this.errorMessage = message;
            this.errorDetails = details;
            this.showRetryButton = allowRetry;
        },

        async retryHandoff() {
            this.retryLoading = true;
            this.trackAnalytics('handoff_retry');
            
            try {
                await this.processHandoffToken();
            } finally {
                this.retryLoading = false;
            }
        },

        performRedirect() {
            // Priority: custom redirect URL > trim editor > home
            if (this.redirectUrl) {
                try {
                    // Validate redirect URL for security
                    const url = new URL(this.redirectUrl);
                    if (url.origin === window.location.origin) {
                        // Same origin - safe to redirect
                        this.$router.push(this.redirectUrl.replace(window.location.origin, ''));
                        return;
                    }
                } catch (error) {
                    console.warn('Invalid redirect URL, using default:', error);
                }
            }

            // Default redirect to trim editor (home page with trims)
            this.$router.push({ name: 'trims' });
        },

        redirectToHome() {
            this.$router.push({ name: 'home' });
        },

        trackAnalytics(event, additionalData = {}) {
            // Track handoff events for debugging and analytics
            const analyticsPayload = {
                event,
                ...this.analyticsData,
                ...additionalData
            };

            console.log('Handoff Analytics:', analyticsPayload);

            // TODO: Replace with actual analytics service integration
            // Example: this.$analytics.track(event, analyticsPayload);
            
            // For now, just log to console for debugging
            if (window.gtag) {
                window.gtag('event', event, analyticsPayload);
            }
        }
    },

    async mounted() {
        // Start handoff process immediately when component mounts
        await this.processHandoffToken();
    },

    beforeUnmount() {
        // Clean up any pending timeouts
        // (Vue 3 automatically handles this, but good practice)
    }
}
</script>

<style scoped>
.handoff-container {
    max-width: 500px;
    margin: 50px auto;
    padding: 20px;
    text-align: center;
}

.loading-section,
.error-section,
.success-section {
    padding: 40px 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.loading-section {
    background-color: #f8f9fa;
    border: 2px solid #e9ecef;
}

.error-section {
    background-color: #fff5f5;
    border: 2px solid #fed7d7;
}

.success-section {
    background-color: #f0fff4;
    border: 2px solid #9ae6b4;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

.loading-spinner.small {
    width: 20px;
    height: 20px;
    border-width: 2px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.error-icon,
.success-icon {
    font-size: 48px;
    margin-bottom: 20px;
}

.error-message {
    margin: 20px 0;
}

.error-details {
    margin-top: 10px;
    color: #666;
    font-style: italic;
}

.error-actions {
    margin-top: 30px;
    display: flex;
    gap: 15px;
    justify-content: center;
    flex-wrap: wrap;
}

.btn {
    padding: 10px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
    text-decoration: none;
    display: inline-block;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-primary {
    background-color: #007bff;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #0056b3;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover:not(:disabled) {
    background-color: #545b62;
}

h3 {
    margin-bottom: 15px;
    color: #333;
}

p {
    color: #666;
    line-height: 1.5;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .handoff-container {
        margin: 20px auto;
        padding: 15px;
    }

    .loading-section,
    .error-section,
    .success-section {
        padding: 30px 15px;
    }

    .error-actions {
        flex-direction: column;
    }

    .btn {
        width: 100%;
        max-width: 200px;
    }
}
</style>